import { notificationType } from "@/types/notification";
import { defineStore } from "pinia";
import { watch } from "vue";
import { useUser } from "./user";
import { getListNotification, markAllAsReadNotification, toggleReadValueNotification } from "@/api/apiclient/notification";

export const useNotification = defineStore("notification", {
  state: () => ({
    notifications: {
      unreads: [] as notificationType[],
      reads: [] as notificationType[],
    },
    refreshTimer: null as number | null,
    tick: 0, // Forza il refresh delle date 'time ago'
  }),
  actions: {
    async init(): Promise<void> {
      const response = await getListNotification();
      this.notifications.unreads = response.filter((n) => !n.read);
      this.notifications.reads = response.filter((n) => n.read);
      this.tick++; // forza ricalcolo
    },
    // Polling ogni 2 minuti
    startAutoRefresh(): void {
      if (this.refreshTimer) return;

      this.refreshTimer = window.setInterval(
        () => {
          this.init();
        },
        2 * 60 * 1000
      );
    },

    stopAutoRefresh(): void {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    setupAuthWatcher() {
      const userStore = useUser();

      watch(
        () => userStore.isLoggedIn,
        async (loggedIn) => {
          if (loggedIn) {
            await this.init();
            this.startAutoRefresh();
          } else {
            this.stopAutoRefresh();
          }
        },
        { immediate: true }
      );
    },

    async updateNotification(id: number | string, read: boolean) {
      // Salva lo stato attuale (rollback se serve)
      const prevUnreads = [...this.notifications.unreads];
      const prevReads = [...this.notifications.reads];

      try {
        // Aggiorna subito lo store (optimistic update)
        if (read) {
          const notification = this.notifications.unreads.find((n) => n.id === id);
          if (notification) {
            const updated = { ...notification, read: true };
            this.notifications.unreads = this.notifications.unreads.filter((n) => n.id !== id);
            this.notifications.reads.unshift(updated);
          }
        } else {
          const notification = this.notifications.reads.find((n) => n.id === id);
          if (notification) {
            const updated = { ...notification, read: false };
            this.notifications.reads = this.notifications.reads.filter((n) => n.id !== id);
            this.notifications.unreads.unshift(updated);
          }
        }
        await toggleReadValueNotification(Number(id), { read: read });
      } catch (err) {
        // Rollback se errore
        this.notifications.unreads = prevUnreads;
        this.notifications.reads = prevReads;
        throw err;
      }
    },
    async markAllAsRead() {
      // Salva stato precedente (per rollback)
      const prevUnreads = [...this.notifications.unreads];
      const prevReads = [...this.notifications.reads];

      try {
        // Aggiorna subito lo store (optimistic)
        const updated = this.notifications.unreads.map((n) => ({
          ...n,
          read: true,
        }));

        this.notifications.reads = [...updated, ...this.notifications.reads];
        this.notifications.unreads = [];

        await markAllAsReadNotification();
      } catch (err) {
        // Rollback
        this.notifications.unreads = prevUnreads;
        this.notifications.reads = prevReads;
        throw err;
      }
    },
  },
  getters: {
    getIcon() {
      return (icon: string): string => {
        switch (icon) {
          case "appointment":
            return "calendar_done";
          case "project":
            return "condo_house";
          case "network":
            return "rete";
          default:
            return "bell";
        }
      };
    },
  },
});
